import { createSelector } from 'reselect';

import localization from '../../shared/strings';
import {
  pickMetaFields,
  pickPricingFields,
  pickProductCoreFields,
  pickProductInventoryFields,
  pickProductSeoFields,
  pickShippingFields,
  pickVariantInventoryFields,
} from '../products/helpers/pickAggregatedFieldGroup';
import {
  getEntitySelectionContext,
  isMasterSelectionContext,
} from '../products/helpers/selectionContext';
import { parseSelectionKey } from '../products/helpers/selectionKey';
import { resolveAggregatedVariantContext } from '../products/helpers/resolveAggregatedVariantContext';
import { selectProductSelection, selectProductSelectionStats } from './productSelectionSelectors';

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;

const normalizeContextKey = (context) => {
  if (!context) return null;
  if (context.type === 'master') return 'master';
  return `${context.type}:${context.channelId}:${context.recordState}`;
};

const resolveLayerAttributesTitle = (selection) => {
  const layerKeys = selection.keys
    .map((key) => normalizeContextKey(getEntitySelectionContext(selection.byKey[key])))
    .filter(Boolean);
  const uniqueLayerKeys = new Set(layerKeys);

  if (uniqueLayerKeys.size > 1) {
    return aggregatedL10n.mixedLayerAttributesTitle;
  }

  const { context } = selection;

  if (isMasterSelectionContext(context)) {
    return aggregatedL10n.masterLayerAttributesTitle;
  }

  if (context?.recordState === 'draft') {
    return aggregatedL10n.draftLayerAttributesTitle;
  }

  if (context?.recordState === 'published') {
    return aggregatedL10n.publishedLayerAttributesTitle;
  }

  return aggregatedL10n.mixedLayerAttributesTitle;
};

export const selectAggregatedProductDetailsState = (state) => (
  state.products.ui.aggregatedDetails
);

export const selectShouldShowAggregatedDetails = createSelector(
  selectProductSelection,
  (selection) => selection.keys.length >= 2,
);

export const selectAggregatedDetailsStatus = createSelector(
  selectAggregatedProductDetailsState,
  (aggregatedDetails) => aggregatedDetails.status,
);

export const selectAggregatedDetailsError = createSelector(
  selectAggregatedProductDetailsState,
  (aggregatedDetails) => aggregatedDetails.error,
);

export const selectAggregatedDetailsData = createSelector(
  selectAggregatedProductDetailsState,
  (aggregatedDetails) => aggregatedDetails.data,
);

const selectAggregatedProductMap = createSelector(
  selectAggregatedDetailsData,
  (data) => data?.product || null,
);

const selectAggregatedVariantMap = createSelector(
  selectAggregatedDetailsData,
  (data) => data?.variant || null,
);

export const selectAggregatedProductCoreFields = createSelector(
  selectAggregatedProductMap,
  pickProductCoreFields,
);

export const selectAggregatedVariantPricingFields = createSelector(
  selectAggregatedVariantMap,
  pickPricingFields,
);

export const selectAggregatedProductPricingFields = createSelector(
  selectAggregatedProductMap,
  pickPricingFields,
);

export const selectAggregatedProductInventoryFields = createSelector(
  selectAggregatedProductMap,
  pickProductInventoryFields,
);

export const selectAggregatedProductShippingFields = createSelector(
  selectAggregatedProductMap,
  pickShippingFields,
);

export const selectAggregatedProductMetaFields = createSelector(
  selectAggregatedProductMap,
  pickMetaFields,
);

export const selectAggregatedVariantInventoryFields = createSelector(
  selectAggregatedVariantMap,
  pickVariantInventoryFields,
);

export const selectAggregatedVariantShippingFields = createSelector(
  selectAggregatedVariantMap,
  pickShippingFields,
);

export const selectAggregatedVariantMetaFields = createSelector(
  selectAggregatedVariantMap,
  pickMetaFields,
);

export const selectAggregatedProductSeoFields = createSelector(
  selectAggregatedProductMap,
  pickProductSeoFields,
);

export const selectAggregatedProductKeywordsField = createSelector(
  selectAggregatedProductCoreFields,
  (core) => core?.keywords || null,
);

export const selectIsAggregatedMixedEntitySelection = createSelector(
  selectProductSelectionStats,
  (stats) => stats.products > 0 && stats.variants > 0,
);

const LISTING_ENTITY_TYPES = new Set(['listing', 'listingVariant']);

export const selectAggregatedMetaLayer = createSelector(
  selectProductSelection,
  (selection) => {
    const hasListingEntity = selection.keys.some(
      (key) => LISTING_ENTITY_TYPES.has(selection.byKey[key]?.type),
    );

    if (hasListingEntity || !isMasterSelectionContext(selection.context)) {
      return 'listing';
    }

    return 'master';
  },
);

export const selectAggregatedPanelTitle = createSelector(
  selectProductSelection,
  (selection) => {
    if (selection.keys.length < 1) return null;
    return resolveLayerAttributesTitle(selection);
  },
);

/** @deprecated use selectAggregatedPanelTitle */
export const selectAggregatedLayerAttributesTitle = selectAggregatedPanelTitle;

export const selectAggregatedVariantContext = (state) => (
  resolveAggregatedVariantContext(state)
);

export const selectFirstSelectedProductId = createSelector(
  selectProductSelection,
  (selection) => {
    const masterKey = selection.keys.find((key) => selection.byKey[key]?.type === 'master');
    if (masterKey) return parseSelectionKey(masterKey)._id;

    const listingKey = selection.keys.find((key) => selection.byKey[key]?.type === 'listing');
    if (listingKey) return parseSelectionKey(listingKey)._id;

    return null;
  },
);
